import { Button } from '@agms/ui'
import { APPOINTMENT_TYPES, type AppointmentFormState } from '../../lib/appointmentForm'
import type { AppointmentType } from '../../lib/api'

export interface AssigneeOption {
  id: string
  name: string
}

interface AppointmentFormProps {
  values: AppointmentFormState
  onChange: (values: AppointmentFormState) => void
  onSubmit: () => void
  submitLabel: string
  pending?: boolean
  error?: string | null
  canAssign: boolean
  assignees: AssigneeOption[]
  currentAssignee?: AssigneeOption | null
  reminderHint: string
  showLocation: boolean
  showNotes: boolean
  framed?: boolean
}

export function AppointmentForm({
  values,
  onChange,
  onSubmit,
  submitLabel,
  pending = false,
  error,
  canAssign,
  assignees,
  currentAssignee,
  reminderHint,
  showLocation,
  showNotes,
  framed = true,
}: AppointmentFormProps) {
  const assigneeOptions = [...assignees]
  if (currentAssignee && !assigneeOptions.some((user) => user.id === currentAssignee.id)) {
    assigneeOptions.unshift(currentAssignee)
  }

  const set = (patch: Partial<AppointmentFormState>) => onChange({ ...values, ...patch })

  return (
    <form
      className={framed ? 'space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3' : 'space-y-3'}
      onSubmit={(event) => {
        event.preventDefault()
        onSubmit()
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm font-medium text-slate-700">
          Type
          <select
            value={values.type}
            onChange={(event) => set({ type: event.target.value as AppointmentType })}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          >
            {APPOINTMENT_TYPES.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm font-medium text-slate-700">
          Date and time
          <input
            type="datetime-local"
            required
            value={values.startsAt}
            onChange={(event) => set({ startsAt: event.target.value })}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="block text-sm font-medium text-slate-700">
          Duration (minutes)
          <input
            type="number"
            min={5}
            max={1440}
            required
            value={values.duration}
            onChange={(event) => set({ duration: event.target.value })}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </label>
        {showLocation && (
          <label className="block text-sm font-medium text-slate-700">
            Location or meeting link
            <input
              type="text"
              value={values.location}
              maxLength={1000}
              onChange={(event) => set({ location: event.target.value })}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </label>
        )}
      </div>
      {showNotes && (
        <label className="block text-sm font-medium text-slate-700">
          Notes
          <textarea
            value={values.notes}
            maxLength={5000}
            rows={2}
            onChange={(event) => set({ notes: event.target.value })}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </label>
      )}
      <label className="block text-sm font-medium text-slate-700">
        Reminder lead time (minutes before)
        <input
          type="number"
          min={1}
          max={10080}
          value={values.reminder}
          onChange={(event) => set({ reminder: event.target.value })}
          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
      </label>
      <p className="text-xs text-slate-500">{reminderHint}</p>
      {canAssign && (
        <label className="block text-sm font-medium text-slate-700">
          Assign to
          <select
            value={values.assigneeId}
            onChange={(event) => set({ assigneeId: event.target.value })}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">Me</option>
            {assigneeOptions.map((user) => (
              <option key={user.id} value={user.id}>{user.name}</option>
            ))}
          </select>
        </label>
      )}
      {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
      <Button type="submit" isLoading={pending}>{submitLabel}</Button>
    </form>
  )
}
